import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { DateConstants } from '@app/constants/date-constants';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { QuoteRequestIndexItem } from '@app/quote-index/models/quote-request-index-item.model';
import { QuoteRequestIndexState } from '@app/quote-index/state/quote-index.state';
import { UserRole, UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { Store } from '@ngrx/store';
import { distinct, State } from '@progress/kendo-data-query';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { QuoteGridService } from '@app/quote-index/services/quote-grid.service';
import { AppState } from '@app/state/app.state';
import { isDynamicSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { loggedInUtilityHubIdentitySelector } from '@app/state/app.selectors';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { ConfigService } from '@app/shared/services/config.service';
import { Subscription } from 'rxjs';
import {
  FetchRenewalsQueue,
  FetchRenewalsQueueFailure,
  FetchRenewalsQueueSuccess,
} from '@app/renewals/state/actions/renewals-module.actions';

@Component({
  selector: 'app-renewals-grid',
  templateUrl: './renewals-grid.component.html',
  styleUrls: ['./renewals-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RenewalsGridComponent implements OnInit, OnDestroy {
  public Commodity = CommodityType;
  public dateFormat = DateConstants.LOCALE_FORMAT_SHORT();
  public organisationId: string;
  public powwrEnabled: boolean;
  public userIdentity: UtilityHubIdentity;
  public QuoteTypeValues = [
    { name: 'Matrix', id: 'Matrix' },
    { name: 'Custom', id: 'Custom' },
  ];
  public statsFilterValue: string[] = [];
  public quoteStatuses = [
    { name: 'In Progress', id: 'InProgress' },
    { name: 'Offer Accepted', id: 'QuoteAccepted' },
    { name: 'Offer Rejected', id: 'QuoteRejected' },
    { name: 'Quote Sent', id: 'QuoteRequestApproved' },
    { name: 'Quote Expired', id: 'QuoteExpired' },
    { name: 'Quote Generated', id: 'QuoteGenerated' },
    {
      name: 'Quote Requested',
      id: 'QuoteRequested',
      filterValues: [QuoteRequestStatus.QuoteRequested, QuoteRequestStatus.QuoteExpiredUnderReview],
    },
    {
      name: 'Quote Enrolled',
      id: 'QuoteEnrolled',
      filterValues: [
        QuoteRequestStatus.PartiallyEnrolled,
        QuoteRequestStatus.Enrolled,
        QuoteRequestStatus.ContractLive,
        QuoteRequestStatus.ContractExpiring,
        QuoteRequestStatus.ContractExpired,
        QuoteRequestStatus.ContractDropPending,
        QuoteRequestStatus.ContractDropped,
      ],
    },
    { name: 'Request Rejected', id: 'QuoteRequestRejected' },
    { name: 'Contract Rejected', id: 'DocuSignRejected' },
    { name: 'DocuSign Completed', id: 'DocuSignCompleted' },
    { name: 'Enrollment Pending', id: 'PendingEnrollment' },
    { name: 'Partially Enrolled', id: 'PartiallyEnrolled' },
    { name: 'Enrolled', id: 'Enrolled' },
    { name: 'Contract Live', id: 'ContractLive' },
    { name: 'Contract Expiring', id: 'ContractExpiring' },
    { name: 'Contract Expired', id: 'ContractExpired' },
    { name: 'Contract Drop Pending', id: 'ContractDropPending' },
    { name: 'Contract Dropped', id: 'ContractDropped' },
    {
      name: 'Contract Pending',
      id: 'PendingContract',
      filterValues: [QuoteRequestStatus.PendingContract, QuoteRequestStatus.ContractSigned],
    },
    { name: 'Api Call Failed', id: 'ApiCallFailed' },
    { name: 'Refresh Requested', id: 'RefreshRequested' },
    { name: 'Credit Failed', id: 'CreditFailed' },
    { name: 'Credit Review Required', id: 'CreditReviewRequired' },
    { name: 'Quotes Due Today', id: 'QuoteDueToday' },
  ];

  public cells: any = {
    textAlign: 'left',
  };

  public gridState: State = {
    skip: 0,
    take: 20,
    sort: [
      {
        field: 'QuoteRequestId',
        dir: 'desc',
      },
    ],
    filter: {
      filters: [],
      logic: 'and',
    },
  };
  public gridView: GridDataResult;

  private dateTwoMonthsFromNow = new Date();
  private activeFilters$: Subscription;
  private quoteRequests$: Subscription;

  constructor(
    private store: Store<AppState>,
    public quoteRequestIndexState: QuoteRequestIndexState,
    public configService: ConfigService,
    private router: Router,
    private route: ActivatedRoute,
    public datepipe: DatePipe,
    public quoteGridService: QuoteGridService
  ) {
    this.store.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
      this.userIdentity = user;
      this.organisationId = user.currentOrganisation.id;
      this.getUhConfiguration();
    });
  }

  public getBrokerColumnVisibility(): boolean {
    return (
      this.userIdentity.currentOrganisation.role === UserRole.SupplierManager ||
      this.userIdentity.currentOrganisation.role === UserRole.BrokerManager ||
      this.userIdentity.currentOrganisation.role === UserRole.Supplier
    );
  }

  public getBrokerageColumnVisibility(): boolean {
    return (
      this.userIdentity.currentOrganisation.role === UserRole.SupplierManager ||
      this.userIdentity.currentOrganisation.role === UserRole.Supplier
    );
  }

  public getCreatedByColumnVisibility(): boolean {
    return this.userIdentity.currentOrganisation.role === UserRole.SupplierManager;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const statsFilterValue = [params.key];

      const field = params.field ?? 'QuoteRequestStatus';
      var filters =
        typeof params.type == 'undefined' || params.type == 'string'
          ? this.quoteGridService.generateFilterConditions(statsFilterValue, this.quoteStatuses, field)
          : this.quoteGridService.generateFilterConditionsDate(statsFilterValue, field);

      // Calculate the date two months from now
      this.dateTwoMonthsFromNow.setMonth(this.dateTwoMonthsFromNow.getDay() + 2);

      this.gridState.filter.filters = [
        {
          filters,
          logic: params.type == 'date' ? 'and' : 'or',
        },
      ];
    });

    this.quoteRequestIndexState.setActiveFilter({ searchText: '', request: this.gridState });

    this.activeFilters$ = this.quoteRequestIndexState.getActiveFilters$().subscribe((filters) => {
      this.quoteRequestIndexState.loadQuoteRenewalsRequests(filters);
    });

    //TODO: Modify this to use NGRX instead of the service
    //Currently these dispatches are just siganling the service to make the API call
    this.store.dispatch(new FetchRenewalsQueue());
    this.quoteRequests$ = this.quoteRequestIndexState.getQuoteRequests$().subscribe((data) => {
      if (data) {
        this.store.dispatch(new FetchRenewalsQueueSuccess());
        this.gridView = data;
      }
    });
  }

  ngOnDestroy(): void {
    this.activeFilters$.unsubscribe();
    this.quoteRequests$.unsubscribe();
  }

  // dataStateChange(state: DataStateChangeEvent): void {
  //   this.gridState = state;
  //   if (state.sort[0].dir) state.sort[0].dir = 'desc';
  //   const filter = this.quoteRequestIndexState.getActiveFilters();
  //   this.quoteRequestIndexState.setActiveFilter({
  //     searchText: filter.searchText,
  //     request: state,
  //   });
  // }

  dataStateChange(state: DataStateChangeEvent): void {
    // Preserve the existing filters
    const existingFilters = this.gridState.filter.filters;
    // Update the grid state
    this.gridState = state;
    if (state.sort[0].dir) state.sort[0].dir = 'desc';
    // Merge the existing filters with the new state
    this.gridState.filter.filters = existingFilters;
    const filter = this.quoteRequestIndexState.getActiveFilters();
    this.quoteRequestIndexState.setActiveFilter({
      searchText: filter.searchText,
      request: this.gridState,
    });
  }

  clearFilters() {
    this.gridState.filter = {
      logic: 'and',
      filters: [],
    };
    this.quoteRequestIndexState.setActiveFilter({ request: this.gridState });
  }

  public save(component): void {
    const options = component.workbookOptions();
    const rows = options.sheets[0].rows;

    var quoteStatusCellIndex = rows[0].cells.length - 1;
    var lastUpdatedCellIndex = rows[0].cells.length - 2;
    var createdDateCellIndex = rows[0].cells.length - 3;
    let altIdx = 0;
    rows.forEach((row) => {
      if (row.type === 'data') {
        row.cells[quoteStatusCellIndex].value = this.quoteStatuses.find(
          (element) => element.id === row.cells[quoteStatusCellIndex].value
        )?.name;
        row.cells[lastUpdatedCellIndex].value = this.datepipe.transform(row.cells[lastUpdatedCellIndex].value, this.dateFormat);
        row.cells[createdDateCellIndex].value = this.datepipe.transform(row.cells[createdDateCellIndex].value, this.dateFormat);
        if (altIdx % 2 !== 0) {
          row.cells.forEach((cell) => {
            cell.background = '#aabbcc';
          });
        }
        altIdx++;
      }
    });
    component.save(options);
  }

  getUhConfiguration() {
    this.store.select(isDynamicSelector).subscribe((d) => (this.powwrEnabled = d));
  }

  public distinctPrimitive(fieldName: string): any {
    console.log(fieldName);
    return distinct(this.gridView.data, fieldName).map((item) => item[fieldName]);
  }

  resolveButtonStyle(requests: QuoteRequestIndexItem[]): QuoteRequestIndexItem[] {
    return requests.map((quoteRequest) => {
      const buttonData = this.resolveSelectQuoteRequestButton(quoteRequest);
      quoteRequest.iconSrc = buttonData.iconSrc;
      quoteRequest.label = buttonData.label;
      return quoteRequest;
    });
  }

  public navigateToJourneySelection(quoteRequest: QuoteRequestIndexItem): void {
    this.router.navigate(['renewals/', 'select-journey', quoteRequest.quoteRequestId.toString()]);
  }

  public resolveSelectQuoteRequestButton(quoteRequest: QuoteRequestIndexItem) {
    switch (quoteRequest.quoteRequestStatus) {
      case QuoteRequestStatus.QuoteRejected:
        return {
          label: 'View',
          iconSrc: '/assets/icons/icon-view.svg',
        };
      case QuoteRequestStatus.QuoteRequestApproved:
        if (this.userIdentity.currentOrganisation.role === UserRole.SupplierManager) {
          return {
            label: 'View',
            iconSrc: '/assets/icons/icon-view.svg',
          };
        } else {
          return {
            label: 'Review',
            iconSrc: '/assets/icons/icon-review.svg',
          };
        }
      case QuoteRequestStatus.QuoteRequested:
      case QuoteRequestStatus.QuoteExpiredUnderReview:
        if (this.userIdentity.currentOrganisation.role === UserRole.SupplierManager) {
          return {
            label: 'Review',
            iconSrc: '/assets/icons/icon-review.svg',
          };
        } else {
          return {
            label: 'View',
            iconSrc: '/assets/icons/icon-view.svg',
          };
        }
      case QuoteRequestStatus.QuoteExpired:
        return {
          label: 'Requote',
          iconSrc: '/assets/icons/icon-redo.svg',
        };
      case QuoteRequestStatus.QuoteRequestRejected: {
        if (this.userIdentity.currentOrganisation.role === UserRole.SupplierManager) {
          return {
            label: 'View',
            iconSrc: '/assets/icons/icon-view.svg',
          };
        } else {
          return {
            label: 'Edit',
            iconSrc: '/assets/icons/icon-edit.svg',
          };
        }
      }
      case QuoteRequestStatus.QuoteGenerated:
        if (this.userIdentity.currentOrganisation.role === UserRole.SupplierManager) {
          return {
            label: 'View',
            iconSrc: '/assets/icons/icon-view.svg',
          };
        } else {
          return {
            label: 'Review',
            iconSrc: '/assets/icons/icon-review.svg',
          };
        }
      default:
        return {
          label: 'Edit',
          iconSrc: '/assets/icons/icon-edit.svg',
        };
    }
  }
}
